import React, { useContext, useEffect, useState } from 'react';
import { IPaginationParams } from '../../services/types';
import { AppContext } from '../../context/AppContextProvider';
import TableComponent, { IColumnProps } from '../../components/TableComponent';
import ButtonActionsComponent from '../../components/ButtonActionsComponent';
import ButtonComponent from '../../components/ButtonComponent';
import { Add } from '@mui/icons-material';
import ConfirmDeleteModalComponent from '../../components/ConfirmDeleteModalComponent';
import {
  ICommunicationFilterParams,
  ICommunicationProps,
  ICommunicationSimpleProps,
} from '../../services/CommunicationService/types';
import {
  useGetAllCommunicationsByLicenseAsync,
  useGetAllCommunicationsByLicenseCountAsync,
} from '../../services/CommunicationService/hooks/useQuery';
import {
  useDeleteCommunicationAsync,
  useGetCommunicationByIdAsync,
} from '../../services/CommunicationService/hooks/useMutation';
import ImageModalComponent from '../../components/ImageModalComponent';
import ImageSimpleColumnComponent from '../../components/ImageSimpleColumnComponent';
import dayjs from 'dayjs';
import CommunicationEditModal from './_components/CommunicationEditModal';

const CommunicationList = () => {
  const [filter, setFilter] = useState<ICommunicationFilterParams>({
    search: '',
  });
  const [pagination, setPagination] = useState<IPaginationParams>({
    pageNumber: 1,
    pageSize: 10,
  });
  const [isShowImageModal, setIsShowImageModal] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [isVisibleEditModal, setIsVisibleEditModal] = useState<boolean>(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] =
    useState<boolean>(false);
  const [selectedToEdit, setSelectedToEdit] = useState<ICommunicationProps>();
  const [selectedToDelete, setSelectedToDelete] =
    useState<ICommunicationSimpleProps>();
  const {
    data: communications,
    refetch: refetchCommunications,
    isLoading: isLoadingCommunications,
  } = useGetAllCommunicationsByLicenseAsync(pagination, filter);
  const {
    data: totalCommunications,
    refetch: refetchTotalCommunications,
    isLoading: isLoadingTotalCommunications,
  } = useGetAllCommunicationsByLicenseCountAsync(pagination, filter);
  const { mutateAsync: mutateGetCommunication } =
    useGetCommunicationByIdAsync();
  const { mutateAsync: mutateDeleteCommunication } =
    useDeleteCommunicationAsync();

  const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

  const columns: IColumnProps[] = [
    { id: 'name', label: 'Nome', width: 'auto' },
    {
      id: 'initialDate',
      label: 'Início em',
      width: 'auto',
      render: (initialDate) => (
        <>{dayjs(initialDate).utc().format('DD/MM/YYYY')}</>
      ),
    },
    {
      id: 'validUntil',
      label: 'Validade',
      width: 'auto',
      render: (validUntil) => (
        <>{dayjs(validUntil).utc().format('DD/MM/YYYY')}</>
      ),
    },
    {
      id: 'image',
      label: 'Imagem',
      width: 'auto',
      align: 'center',
      render: (image) => (
        <ImageSimpleColumnComponent
          url={image}
          onClick={() => handleImageModal(image)}
        />
      ),
    },
    {
      id: 'id',
      label: 'Ações',
      align: 'center',
      width: '0px',
      render: (id) => (
        <ButtonActionsComponent
          key={Number(id)}
          isWithDefaultOptionsList
          handleDelete={() => handleDelete(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const refetchAll = async () => {
    await refetchCommunications();
    await refetchTotalCommunications();
  };

  const handleEdit = async (id: number) => {
    setIsShowLoadingComponent(true);
    await mutateGetCommunication(id)
      .then(setSelectedToEdit)
      .finally(() => setIsShowLoadingComponent(false));
    setIsVisibleEditModal(true);
  };

  const handleDelete = async (id: number) => {
    setSelectedToDelete(communications?.find((i) => i.id === id));
    setIsVisibleDeleteModal(true);
  };

  const handleConfirmDeletion = async () => {
    setIsShowLoadingComponent(true);
    if (selectedToDelete && selectedToDelete.id) {
      await mutateDeleteCommunication(selectedToDelete.id)
        .then(() => {
          setSelectedToDelete(undefined);
          setIsVisibleDeleteModal(false);
          showSnackbar('Comunicado deletado com sucesso!', 'success');
          refetchAll();
        })
        .finally(() => {
          setIsShowLoadingComponent(false);
        });
    }
  };

  const handleCancelDeletion = () => {
    setIsVisibleDeleteModal(false);
    setSelectedToDelete(undefined);
  };

  const handleImageModal = (image: string) => {
    setSelectedImage(image);
    setIsShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setIsShowImageModal(false);
    setSelectedImage('');
  };

  const handleSaveForm = async () => {
    setSelectedToEdit(undefined);
    await refetchAll();
  };

  const handleCloseEditionModal = () => {
    setSelectedToEdit(undefined);
    setIsVisibleEditModal(false);
  };

  useEffect(() => {
    if (communications && totalCommunications) {
      refetchAll();
    }
  }, [pagination, filter]);

  return (
    <>
      <TableComponent
        columns={columns}
        rows={communications ?? []}
        sortByName={'name'}
        isLoading={isLoadingCommunications || isLoadingTotalCommunications}
        title="Comunicados"
        totalCount={totalCommunications}
        onSubmitMainFilter={refetchAll}
        onChangeMainFilter={(val) =>
          setFilter((prev) => ({ ...prev, search: val }))
        }
        searchValue={filter.search}
        onChangePageOrRowsPerPage={setPagination}
        additionalTitleComponent={
          <ButtonComponent
            onClick={() => setIsVisibleEditModal(true)}
            startIcon={<Add />}
          >
            Novo Comunicado
          </ButtonComponent>
        }
      />
      <ConfirmDeleteModalComponent
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
        isVisibleDeleteDialog={isVisibleDeleteModal}
        message={`Deseja deletar o comunicado ${selectedToDelete?.name} ?`}
        setIsVisibleDeleteDialog={setIsVisibleDeleteModal}
        title={'Deletar Comunicado'}
      />
      <ImageModalComponent
        isOpen={isShowImageModal}
        handleClose={() => handleCloseImageModal()}
        imageUrl={selectedImage}
        title={'Imagem do Comunicado'}
        maxWidth={'100%'}
        maxHeight={'100%'}
      />
      <CommunicationEditModal
        isOpen={isVisibleEditModal}
        handleClose={handleCloseEditionModal}
        handleSaveForm={handleSaveForm}
        communicationToEdit={selectedToEdit}
      />
    </>
  );
};

export default CommunicationList;
